import { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import dayjs from "dayjs";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import { useCurrency } from "contexts/CurrencyContext";
import { useToast } from "contexts/ToastContext";
import { brand, destructive } from "utils/designTokens";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";

import SearchField from "components/SearchField";
import ScrollableTableContainer from "components/table/ScrollableTableContainer";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import { offerStatusChipSx, couponCodeChipSx } from "components/table/tableChipStyles";
import CurrencyAmount from "components/currency/CurrencyAmount";

import {
  Box,
  Grid,
  Button,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Switch,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TablePagination
} from "@mui/material";

import CouponFormDialog from "./CouponFormDialog";

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
  expired: "Expired",
  upcoming: "Upcoming"
};

const filterFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 2.5,
    bgcolor: '#fff',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': {
      borderColor: brand.border
    },
    '&:hover fieldset': {
      borderColor: brand.borderHover
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${brand.overlay}`,
      '& fieldset': {
        borderColor: 'primary.main'
      }
    }
  }
};

const getCouponStatus = (coupon) => {
  const now  = dayjs();
  const from = dayjs(coupon.from_datetime);
  const to   = dayjs(coupon.to_datetime);
  if (!coupon.status) return "inactive";
  if (now.isBefore(from)) return "upcoming";
  if (now.isAfter(to))    return "expired";
  return "active";
};

const StatCard = ({ label, value, color }) => (
  <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider" }}>
    <Typography variant="h5" fontWeight={700} color={color || "primary.main"}>{value}</Typography>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
  </Paper>
);

export default function CouponList() {
  const { currency } = useCurrency();
  const { showToast, showApiError } = useToast();

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [formDialog, setFormDialog] = useState({ open: false, id: null, coupon: null });
  const [delDialog, setDelDialog] = useState({ open: false, id: null, code: "" });
  const [deleting, setDeleting] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        offer_type: "coupon",
        page: page + 1,
        size: rowsPerPage
      };
      if (search) params.search = search;
      if (statusFilter !== "all") {
        if (statusFilter === "active" || statusFilter === "inactive") {
          params.status = statusFilter === "active";
        }
      }

      const res = await api.get("/offer/list", { params });
      const responseData = res.data?.data;
      if (responseData && Array.isArray(responseData.offers)) {
        setCoupons(responseData.offers);
        setTotalCount(responseData.pagination?.total ?? responseData.offers.length);
      } else if (Array.isArray(res.data)) {
        setCoupons(res.data);
        setTotalCount(res.data.length);
      } else {
        setCoupons([]);
        setTotalCount(0);
      }
    } catch (err) {
      showApiError(err, "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter, showApiError]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const handleToggleStatus = async (id, currentStatus) => {
    setUpdatingId(id);
    try {
      await api.patch(`/offer/manage/${id}`, { status: !currentStatus });
      showToast(`Coupon ${!currentStatus ? "activated" : "deactivated"} successfully`, { severity: "success" });
      loadCoupons();
    } catch (err) {
      showApiError(err, "Status update failed");
    } finally {
      setUpdatingId(null);
    }
  };

  const openDeleteDialog = (coupon) => {
    setDelDialog({
      open: true,
      id: coupon.id,
      code: coupon.coupon_code || coupon.offer_name_en || "this coupon"
    });
  };

  const handleDeleteConfirm = async () => {
    if (!delDialog.id) return;
    setDeleting(true);
    try {
      await api.delete(`/offer/delete/${delDialog.id}`);
      showToast("Coupon deleted successfully", { severity: "success" });
      setDelDialog({ open: false, id: null, code: "" });
      loadCoupons();
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const filteredCoupons = coupons.filter((c) => {
    const status = getCouponStatus(c);
    if (statusFilter === "upcoming" && status !== "upcoming") return false;
    if (statusFilter === "expired" && status !== "expired") return false;
    return true;
  });

  const stats = {
    total: totalCount || coupons.length,
    active: coupons.filter((c) => getCouponStatus(c) === "active").length,
    upcoming: coupons.filter((c) => getCouponStatus(c) === "upcoming").length,
    expired: coupons.filter((c) => getCouponStatus(c) === "expired").length
  };

  return (
    <Box>
      <PageHeader
        title="Coupons"
        subtitle="Manage promotional discount coupon codes."
        count={stats.total}
        actionLabel="Create Coupon"
        actionIcon={AddIcon}
        onAction={() => setFormDialog({ open: true, id: null, coupon: null })}
      />

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard label="Total Coupons" value={stats.total} />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Active Coupons" value={stats.active} color="success.main" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Upcoming Coupons" value={stats.upcoming} color="info.main" />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard label="Expired Coupons" value={stats.expired} color="error.main" />
        </Grid>
      </Grid>

      <MainCard contentSX={{ p: 0 }} sx={{ borderRadius: 3, position: "relative" }}>
        {(loading || updatingId) && (
          <LinearProgress
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 10,
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12
            }}
          />
        )}

        {/* Filters bar */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center"
          }}
        >
          <SearchField
            placeholder="Search coupon code or name…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 260, mb: 0 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 150, ...filterFieldSx }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="upcoming">Upcoming</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
        </Box>

        {loading && coupons.length === 0 ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <ScrollableTableContainer sx={{ maxHeight: "calc(100vh - 340px)" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "grey.50", whiteSpace: "nowrap" } }}>
                    <TableCell>Coupon Code</TableCell>
                    <TableCell>Offer Name</TableCell>
                    <TableCell>Discount</TableCell>
                    <TableCell>Min Purchase / Cap</TableCell>
                    <TableCell>Validity</TableCell>
                    <TableCell align="center">First Order</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredCoupons.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 6, color: "text.secondary" }}>
                        No coupons found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCoupons.map((coupon) => {
                      const id = coupon.id;
                      const status = getCouponStatus(coupon);
                      const statusLabel = STATUS_LABELS[status] || status;

                      return (
                        <TableRow key={id} hover sx={{ "& td": { py: 1.5 } }}>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Chip
                              icon={<ConfirmationNumberIcon style={{ fontSize: 16 }} />}
                              label={coupon.coupon_code || "N/A"}
                              size="small"
                              variant="outlined"
                              sx={couponCodeChipSx}
                            />
                          </TableCell>

                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {coupon.offer_name_en}
                              </Typography>
                              {coupon.offer_name_ar && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  display="block"
                                  sx={{ direction: "rtl" }}
                                >
                                  {coupon.offer_name_ar}
                                </Typography>
                              )}
                              {coupon.description && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {coupon.description}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {coupon.offer_value_type === "percentage" ? (
                              <Typography variant="body2" fontWeight={700} sx={{ color: brand.primary }}>
                                {coupon.offer_value}% OFF
                              </Typography>
                            ) : (
                              <CurrencyAmount
                                value={coupon.offer_value}
                                variant="body2"
                                fontWeight={700}
                                color="primary.main"
                              />
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            {coupon.minimum_purchase_amount ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption">Min:</Typography>
                                <CurrencyAmount value={coupon.minimum_purchase_amount} variant="caption" />
                              </Box>
                            ) : (
                              <Typography variant="caption" display="block">
                                Min: No Min
                              </Typography>
                            )}
                            {coupon.offer_value_type === "percentage" && coupon.max_discount_amount && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">Max:</Typography>
                                <CurrencyAmount value={coupon.max_discount_amount} variant="caption" color="text.secondary" />
                              </Box>
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Typography variant="caption" display="block">
                              {dayjs(coupon.from_datetime).format("DD MMM YY, hh:mm A")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              → {dayjs(coupon.to_datetime).format("DD MMM YY, hh:mm A")}
                            </Typography>
                          </TableCell>

                          <TableCell align="center">
                            {coupon.is_first_order ? (
                              <Chip label="1st Order" size="small" color="secondary" variant="outlined" />
                            ) : (
                              "—"
                            )}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Chip label={statusLabel} size="small" sx={offerStatusChipSx(status)} />
                          </TableCell>

                          <TableCell align="center">
                            {updatingId === id ? (
                              <CircularProgress size={20} sx={{ color: brand.primary }} />
                            ) : (
                              <Switch
                                size="small"
                                checked={!!coupon.status}
                                onChange={() => handleToggleStatus(id, coupon.status)}
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: brand.primary,
                                    '&:hover': { bgcolor: 'rgba(31, 91, 64, 0.08)' }
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    bgcolor: brand.primary
                                  }
                                }}
                              />
                            )}
                          </TableCell>

                          <TableCell align="center">
                            <TableActions>
                              <TableActionButton
                                action="edit"
                                onClick={() => setFormDialog({ open: true, id, coupon })}
                              />
                              <TableActionButton
                                action="delete"
                                onClick={() => openDeleteDialog(coupon)}
                              />
                            </TableActions>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </ScrollableTableContainer>

            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 20, 50]}
            />
          </>
        )}
      </MainCard>

      <CouponFormDialog
        open={formDialog.open}
        couponId={formDialog.id}
        initialCoupon={formDialog.coupon}
        onClose={() => setFormDialog({ open: false, id: null, coupon: null })}
        onSaved={loadCoupons}
      />

      <Dialog
        open={delDialog.open}
        onClose={() => !deleting && setDelDialog({ open: false, id: null, code: "" })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Coupon</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove coupon <strong>{delDialog.code}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setDelDialog({ open: false, id: null, code: "" })}
            disabled={deleting}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": { borderColor: brand.borderHover, bgcolor: brand.overlay }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
            sx={{
              bgcolor: destructive.main,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              boxShadow: destructive.shadow,
              "&:hover": { bgcolor: destructive.dark, boxShadow: destructive.shadow },
              "&.Mui-disabled": { bgcolor: "rgba(183, 28, 28, 0.45)", color: "#fff" }
            }}
          >
            {deleting ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
